import { useCallback, useEffect, useState } from 'react'

import { AddEditModal } from './AddEditModal'
import { getTranslation } from '../../../account/services/auth'
import Loader from '../../../common/components/Loader'
import { Currency } from '../../../common/models'
import { IColumn, Table } from '../../../core/components/Table'
import { formatDateTime } from '../../../core/constants/times'
import { useCheckPermission } from '../../../core/hooks/use-check-permission'
import { Referral, getReferrals } from '../../../core/services/referrals'
import translate from '../../../core/services/translate'
import { Checkbox } from '@/components/ui'

export type ReferralType = 'all' | 'single'

export type ReferralTableProps = {
  visible?: boolean
  setVisible?: (visible: boolean) => void
  type: ReferralType
  currency: Currency | null
}

export const ReferralTable = (props: ReferralTableProps): JSX.Element => {
  const { visible, setVisible, type, currency } = props
  const { hasEditPermission } = useCheckPermission({ page: 'REFERRALS' })

  const [loading, setLoading] = useState<boolean>(false)
  const [item, setItem] = useState<Referral>()
  const [isCheckAll, setIsCheckAll] = useState(false)
  const [data, setData] = useState<Referral[]>([])
  const language = getTranslation()
  const getData = useCallback(async () => {
    try {
      setLoading(true)
      const { data, status } = await getReferrals(
        type === 'all' ? 'ALL_USERS' : 'SINGLE_USER',
      )
      if (status === 200) {
        setData(data)
      }
    } catch (err) {
      console.log(err)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    getData()
  }, [getData])

  const [isCheck, setIsCheck] = useState<string[]>([])

  const handleSelectAll = () => {
    if (!hasEditPermission) {
      return
    }
    setIsCheckAll(!isCheckAll)
    setIsCheck?.(data.map((voucher) => voucher?.id || ''))
    if (isCheckAll) {
      setIsCheck?.([])
    }
  }

  const handleClick = (id: string, checked: boolean) => {
    if (!hasEditPermission) {
      return
    }
    if (checked) {
      setIsCheck?.([...isCheck, id])
    } else {
      setIsCheck?.(isCheck.filter((item) => item !== id))
    }
  }

  const columns: IColumn[] = [
    {
      title: (
        <Checkbox
          disabled={!hasEditPermission}
          onCheckedChange={handleSelectAll}
          checked={isCheckAll}
        />
      ),
      accessor: '',
      render: (row) => (
        <Checkbox
          disabled={!hasEditPermission}
          onCheckedChange={(checked: boolean) => handleClick(row.id, checked)}
          checked={isCheck?.includes(row.id)}
        />
      ),
      width: '16px',
    },
    {
      title: translate(language.value, 'columns.id'),
      accessor: 'id',
      sortable: true,
      render: (row) => row.id.substring(0, 8),
    },
    {
      title: translate(language.value, 'columns.ticketName'),
      accessor: 'name',
      sortable: true,
      render: (row) => row.ticketName,
    },
    {
      title: translate(language.value, 'columns.startDate'),
      accessor: 'startDate',
      sortable: true,
      render: (row) =>
        row.startDate ? <span>{formatDateTime(row.startDate)}</span> : '-',
    },
    {
      title: translate(language.value, 'columns.endDate'),
      accessor: 'endDate',
      sortable: true,
      render: (row) =>
        row.endDate ? <span>{formatDateTime(row.endDate)}</span> : '-',
    },
    {
      title: translate(language.value, 'columns.maxTicketsPerOrder'),
      accessor: 'maxPerOrder',
      sortable: true,
      render: (row) => row.maxPerOrder,
    },
    {
      title: translate(language.value, 'columns.maxRewardInRUNA'),
      accessor: 'maxReward',
      sortable: true,
      render: (row) => row.maxReward,
    },
    {
      title: translate(language.value, 'columns.addToBag'),
      accessor: 'addToBag',
      sortable: true,
      render: (row) => <Checkbox checked={row.addToBag} disabled />,
    },

    {
      title: translate(language.value, 'columns.published'),
      accessor: 'published',
      sortable: true,
      render: (row) => <Checkbox checked={row.published} disabled />,
    },

    {
      title: translate(language.value, 'columns.status'),
      accessor: 'status',
      render: (row: Referral) => (
        <>
          {row.published && new Date().getTime() > row.endDate ? (
            <span className="text-accent-3">
              {translate(language.value, 'common.inactive')}
            </span>
          ) : (
            <span className="text-green-600">
              {translate(language.value, 'common.active')}
            </span>
          )}
        </>
      ),
    },
    {
      title: translate(language.value, 'columns.view'),
      accessor: '',
      render: (row) => (
        <a
          className="view text-accent-1 underline cursor-pointer"
          onClick={() => {
            if (!hasEditPermission) {
              return
            }
            setVisible?.(true)
            setItem(row)
          }}
        >
          {translate(language.value, 'columns.view')}
        </a>
      ),
    },
  ]

  if (type === 'single') {
    const nameColumn = {
      title: translate(language.value, 'columns.name'),
      accessor: 'name',
      sortable: true,
      render: (row: Referral) => (
        <div className="flex flex-col">
          <span>{`${row.referralDto.firstName} ${row.referralDto.lastName}`}</span>
          <span>{row.referralDto.phone}</span>
          <span>{row.referralDto.email}</span>
          <span>
            {translate(language.value, 'referral.referralCode')}:{' '}
            <span className="font-bold">{row.referralDto.referralCode}</span>
          </span>
        </div>
      ),
    }

    columns.splice(2, 0, nameColumn)
  }

  return (
    <>
      {loading && <Loader />}
      <Table
        isCheck={isCheck}
        columns={columns}
        data={data}
        className="text-nowrap"
      />
      <AddEditModal
        currency={currency}
        type={type}
        visible={visible}
        handleCloseModal={() => {
          setVisible?.(false)
          setItem?.(undefined)
        }}
        item={item}
        onClose={() => {
          getData()
        }}
      />
    </>
  )
}
